/**
 * API-unreachable banner (#325) — the honest-degradation chrome that tells the
 * operator the COP cannot confirm the board, so silence is NOT safety.
 *
 * Mirrors the #254 FeedDownChip's truth-in-feeds register but in a HEAVIER key: the
 * feed chip says "one layer's daemon is dark"; THIS says "the whole API is
 * unreachable — everything below may be stale or missing". So it is a full-width,
 * STATIC (never pulses — the motion budget is reserved for live-camera + L3/L4
 * markers), top-of-chrome strip in a fault-red register, not a small inline chip.
 *
 * It asserts UNREACHABILITY ONLY: it never fabricates a count and never implies an
 * all-clear (all copy routes through `apiHealthVocabulary`, pinned by
 * apiHealthVocabulary.lock.test.ts). Renders nothing while the API is reachable.
 */
import { apiUnreachableBanner } from './apiHealthVocabulary';

interface ApiHealthBannerProps {
  /** From useApiHealth — false only after ≥3 consecutive hard failures. */
  reachable: boolean;
  /** Browser-clock epoch ms of the last confirmed reach, or null if never confirmed. */
  lastReachedAt: number | null;
  /** Injectable clock (epoch ms) for deterministic age math in tests. Defaults to now. */
  now?: number;
}

/** Local HH:MM of the last confirmed reach. */
function hhmm(epochMs: number): string {
  return new Date(epochMs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function ApiHealthBanner({
  reachable,
  lastReachedAt,
  now,
}: ApiHealthBannerProps): JSX.Element | null {
  if (reachable) return null;
  const nowMs = now ?? Date.now();
  const label = lastReachedAt !== null ? hhmm(lastReachedAt) : null;
  const ageMs = lastReachedAt !== null ? Math.max(0, nowMs - lastReachedAt) : null;
  return (
    <div
      data-testid="api-health-banner"
      role="alert"
      aria-live="assertive"
      className="w-full shrink-0 border-b-2 border-red-500 bg-red-950/95 px-4 py-2 text-center text-[13px] font-semibold text-red-100"
    >
      {apiUnreachableBanner(label, ageMs)}
    </div>
  );
}
